// panelCard.styles — the approved direction's visual language.
//
// This deliberately matches the EXISTING OR Board rather than the Epic
// screenshot the first attempt was modelled on. That was the whole mistake: a
// reference was treated as a template, and the result was a screen from a
// different product wearing this one's data. Light page, white rounded cards,
// soft tinted section panels, navy accent, red for alarm only.
//
// Plain px here, not the `--u` scale of the rejected panel: this is a desk
// surface first, and the app around it is sized in px.

// ⚠️ THIS WAS A SECOND DESIGN SYSTEM. `BoardAPage`, `BoardBPage` and
// `BoardCPage` imported ZERO tokens and styled themselves entirely from the
// literals below — Tailwind-slate greys beside the app's blue-grey ones, a page
// ground ONE HEX off `surface.base`, a 10px radius nothing else used, and
// `#94a3b8` as label text at **2.56:1**, which fails AA badly.
//
// It was never a decision, just drift: the palette was written from scratch when
// the panel direction was rebuilt, and it happened to land very close to the real
// tokens without being them. `navy` was ALREADY byte-identical to
// `accent.primary`, and `card` to `surface.card` — proof the two were meant to be
// the same thing.
//
// So `C` now DERIVES from the tokens instead of shadowing them. It stays as a
// named object because ~380 lines of styles reference `C.*`, and rewriting those
// call sites would be a large diff for no visual gain; this way one file
// reconciles the palettes and every board follows.
//
// Divergences below are deliberate and annotated. Anything not annotated should
// come from `tokens`.
import tokens from "../../theme/tokens";

const { color, radius } = tokens;

export const C = {
  page: color.surface.base,   // was #e8edf5 — one hex off surface.base (#e9eef5)
  card: color.surface.card,   // was #ffffff — already identical
  line: color.border.default, // was #dde4ee — noticeably softer than every other
                              // card border in the app; the token is documented
                              // as "crisper, clearly visible on the new ground"
  // No token for a hairline yet. Kept as a literal, and deliberately lighter than
  // `line` for internal rules that must not compete with a card edge.
  lineSoft: "#eaeff7",
  radius: radius.lg,          // was 10 — a value nothing else in the app used

  // `ink` and `inkStrong` were #1e293b and #0f172a: two near-blacks a reader
  // cannot tell apart, so the hierarchy they implied was nominal. Collapsed onto
  // the real ink, with weight carrying emphasis instead of a 2% lightness step.
  ink: color.text.primary,
  inkStrong: color.text.primary,
  muted: color.text.secondary, // was #64748b → #4a5280, 6.41:1 on the page ground
  // ⚠️ THE AA FIX. #94a3b8 on this ground is 2.56:1 and was being used as LABEL
  // TEXT. text.muted is 5.49:1 on base and 6.40:1 on card.
  mutedSoft: color.text.muted,

  navy: color.accent.primary, // was #1e40af — already identical
  navySoft: "#e8eeff",        // no token for a tinted accent ground yet
  navyLine: "#c7d5f7",        // no token for a tinted accent border yet

  alarm: "#b91c1c",
  alarmBg: "#fee2e2",
  alarmLine: "#f4b4b4",

  warn: "#a15c07",
  warnBg: "#fef3c7",
  warnLine: "#eecf87",

  good: "#15803d",
  goodBg: "#dcfce7",
  goodLine: "#a7e3bd",

  stage: "#6d28d9",
  stageBg: "#f1ecfe",
  stageLine: "#cdbcf5",
};

// Section tints, matched to the board the user already reads.
export const SECTION_TINT = {
  main: "#dfe6f0",
  endoscopy: "#dcefe1",
  ob: "#f6efd4",
  cath: "#f7e2e8",
  radiology: "#dde9f6",
  default: "#e3e9f2",
};

export function sectionTint(label) {
  const k = String(label || "").toLowerCase();
  if (k.includes("endo")) return SECTION_TINT.endoscopy;
  if (k.includes("ob") || k.includes("baby")) return SECTION_TINT.ob;
  if (k.includes("cath")) return SECTION_TINT.cath;
  if (k.includes("rad") || k.includes("mri")) return SECTION_TINT.radiology;
  if (k.includes("main") || k.includes("or")) return SECTION_TINT.main;
  return SECTION_TINT.default;
}

export const MONO =
  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const styles = {
  page: {
    minHeight: "100vh",
    background: C.page,
    color: C.ink,
    // PanelSheet and FacilityPicker are sized in `calc(var(--u) * n)`, carried
    // over from the first design. Without --u defined here every one of those
    // resolves to zero and their padding, gaps and font sizes silently vanish —
    // which is exactly how "Dimi Borissovacrna" appeared with no space in it.
    // Declared once, at the root of the page that hosts them.
    "--u": "16px",
  },

  // ── The one global rail ────────────────────────────────────────────────────
  // Sticky, two lines, directly under the app bar. These facts must be true
  // before any card below can be trusted, which is also why LIVE lives here and
  // nowhere else — a freshness badge per card would say the same thing twenty
  // times and still not answer "is this board live".
  rail: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: C.card,
    borderBottom: `1px solid ${C.line}`,
    padding: "10px 16px",
    display: "grid",
    gap: 6,
  },
  railTop: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  railBottom: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    flexWrap: "wrap",
    fontSize: 12.5,
    color: C.muted,
  },
  railTitle: { fontSize: 17, fontWeight: 800, color: C.inkStrong },
  railMeta: { fontFamily: MONO, fontSize: 12, color: C.muted },

  flag: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "4px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: `1px solid ${C.line}`,
    background: "#f6f8fc",
    color: C.muted,
  },
  flagAlarm: { background: C.alarmBg, borderColor: C.alarmLine, color: C.alarm },
  flagWarn: { background: C.warnBg, borderColor: C.warnLine, color: C.warn },
  flagGood: { background: C.goodBg, borderColor: C.goodLine, color: C.good },

  // ── Sections ───────────────────────────────────────────────────────────────
  section: { borderRadius: 14, padding: "12px 12px 4px", margin: "14px 16px" },
  sectionHead: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "0 4px 10px",
    fontSize: 11.5,
    fontWeight: 800,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: "#4b5b73",
  },
  countChip: {
    background: "#ffffffcc",
    border: `1px solid ${C.line}`,
    borderRadius: 999,
    padding: "1px 8px",
    fontSize: 11,
    fontWeight: 700,
    color: C.muted,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))",
    gap: 12,
    paddingBottom: 12,
  },

  // ── Card ───────────────────────────────────────────────────────────────────
  card: {
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: C.radius,
    boxShadow: "0 1px 2px rgba(15,23,42,0.05)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  // The alarm owns the TOP EDGE of the card, so severity is legible from the
  // grid without reading any card in full.
  alarmStrip: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
  },
  body: { padding: 12, display: "grid", gap: 9 },

  head: { display: "flex", alignItems: "baseline", gap: 8 },
  room: { fontSize: 17, fontWeight: 800, color: C.inkStrong },
  sectionName: { marginLeft: "auto", fontSize: 11.5, color: C.mutedSoft },
  statusChip: {
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: "0.06em",
    padding: "2px 7px",
    borderRadius: 999,
    border: `1px solid ${C.line}`,
    color: C.muted,
    background: "#f7f9fc",
  },

  staff: { display: "flex", alignItems: "center", gap: 8 },
  avatar: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: C.navySoft,
    border: `1px solid ${C.navyLine}`,
    color: C.navy,
    fontSize: 11,
    fontWeight: 800,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 auto",
  },
  who: { minWidth: 0, display: "grid", gap: 1 },
  name: { display: "block", fontWeight: 700, fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  role: { display: "block", fontSize: 11.5, color: C.muted },
  coverBox: { marginLeft: "auto", textAlign: "right", display: "grid", gap: 0, whiteSpace: "nowrap" },
  coverKey: { display: "block", fontSize: 10, letterSpacing: "0.07em", textTransform: "uppercase", color: C.mutedSoft },
  coverVal: { display: "block", fontFamily: MONO, fontSize: 13, fontWeight: 700 },
  coverShort: { color: C.alarm },

  // ── The time bar ───────────────────────────────────────────────────────────
  // Every card shares ONE day window, so the now-line lands at the same x all
  // the way down the board. That is the single thing a Gantt was giving us —
  // comparing rooms against a common clock — kept without a Gantt.
  track: {
    position: "relative",
    height: 16,
    borderRadius: 5,
    background: "#f1f5fb",
    border: `1px solid ${C.lineSoft}`,
    overflow: "hidden",
  },
  tick: { position: "absolute", top: 0, bottom: 0, width: 1, background: "#e2e8f2" },
  block: {
    position: "absolute",
    top: 2,
    bottom: 2,
    background: "#dbe6fb",
    border: `1px solid #9fb8ea`,
    borderRadius: 4,
    overflow: "hidden",
  },
  blockDone: { position: "absolute", left: 0, top: 0, bottom: 0, background: "#a9c2ee" },
  // The uncovered stretch is hatched AND labelled: it must not depend on colour.
  uncoveredStretch: {
    position: "absolute",
    top: 2,
    bottom: 2,
    background:
      "repeating-linear-gradient(135deg, rgba(185,28,28,0.20) 0 5px, rgba(185,28,28,0.06) 5px 10px)",
    borderTop: `1px solid ${C.alarmLine}`,
    borderBottom: `1px solid ${C.alarmLine}`,
  },
  now: { position: "absolute", top: -1, bottom: -1, width: 2, background: C.inkStrong },
  notch: { position: "absolute", top: -2, bottom: -2, width: 2, background: C.warn },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 11,
    color: C.muted,
    fontFamily: MONO,
  },

  rule: { border: 0, borderTop: `1px solid ${C.lineSoft}`, margin: "1px 0" },

  sup: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, flexWrap: "wrap" },
  supChip: {
    fontSize: 9.5,
    fontWeight: 800,
    padding: "1px 5px",
    borderRadius: 4,
    background: C.navySoft,
    border: `1px solid ${C.navyLine}`,
    color: C.navy,
  },

  // ── Break rail ─────────────────────────────────────────────────────────────
  // Replaces four flat grey pills that could not distinguish "not taken" from
  // "done" without reading colour. Glyph + word + fill.
  breaks: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 5 },
  brk: {
    border: `1px solid ${C.line}`,
    borderRadius: 6,
    padding: "4px 5px",
    display: "grid",
    gap: 1,
    background: "#fbfcfe",
    minWidth: 0,
  },
  brkLab: { fontSize: 9.5, letterSpacing: "0.05em", textTransform: "uppercase", color: C.mutedSoft },
  brkState: { fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },

  // ── Staged relief ──────────────────────────────────────────────────────────
  relief: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: `1px dashed ${C.stageLine}`,
    background: C.stageBg,
    color: C.stage,
    borderRadius: 7,
    padding: "6px 8px",
    fontSize: 12,
    fontWeight: 700,
  },

  acts: { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" },

  // "More" holds the consequential actions. Each one carries a Confirm tag, so
  // the weight of an action is visible before it is opened rather than after.
  more: { position: "relative" },
  moreBody: {
    position: "absolute",
    right: 0,
    bottom: "calc(100% + 6px)",
    minWidth: 214,
    background: C.card,
    border: `1px solid ${C.line}`,
    borderRadius: 9,
    boxShadow: "0 6px 20px rgba(15,23,42,0.14)",
    padding: 6,
    zIndex: 20,
    display: "grid",
    gap: 2,
  },
  moreCap: {
    fontSize: 10,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: C.mutedSoft,
    padding: "4px 8px 2px",
  },
  moreItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    minHeight: 34,
    padding: "0 8px",
    background: "transparent",
    border: "none",
    borderRadius: 6,
    font: "inherit",
    fontSize: 12.5,
    textAlign: "left",
    cursor: "pointer",
    color: C.ink,
  },
  moreTag: {
    marginLeft: "auto",
    fontSize: 9.5,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: C.muted,
    border: `1px solid ${C.line}`,
    borderRadius: 999,
    padding: "1px 6px",
  },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: `1px solid ${C.line}`,
    background: "#ffffff",
    color: C.muted,
    fontSize: 13,
    cursor: "pointer",
    flex: "0 0 auto",
  },
  act: {
    minHeight: 32,
    padding: "0 10px",
    borderRadius: 7,
    border: `1px solid ${C.line}`,
    background: "#ffffff",
    color: C.ink,
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
  },
  actPrimary: { background: C.navy, borderColor: C.navy, color: "#ffffff" },
  actGhost: { background: "transparent", color: C.muted },
};

export default styles;
